(function () {
  const { Component } = window;

  const style = `
    :host {
    --filterModalTextColor: var(--neutral-100, #e7e7e7);
    --filterModalDoneTextColor: var(--neutral-000, #fff);
    --filterModalClearBtnTextColor: var(--blue-800, #4691e3);
    --filterModalClearBtnHoverTextColor: var(--blue-300, #6598d0);

    --filterModalBgColor: var(--neutral-600, #121212);
    --filterModalDoneBgColor: var(--orange-400, #e36600);
    --filterModalDoneHoverBgColor: #e36600cc;
    --filterModalDoneActiveBgColor: #e3660099;
    --filterModalBorderColor: #ffffff14;
    --filterModalFocusRingColor: var(--blue-400, #3478c1);

    --filterModalHeaderFont: var(--font-heading-sm, bold 16px/1.3 sans-serif);
    --filterModalBtnFont: var(--font-label-md, bold 14px/1.4 sans-serif);

      display: none;
      position: fixed;
      inset: 0;
      z-index: 1000;
    }

    :host([open]) {
      display: block;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      background: var(--dark-overlay-80, rgba(0, 0, 0, 0.8));
      opacity: 0;
      transition: opacity 250ms ease-out;
    }

    :host([open]) .backdrop {
      opacity: 1;
    }

    .dialog {
      position: fixed;
      inset: 0;
      background: var(--filterModalBgColor);
      border-radius: 0;
      color: var(--filterModalTextColor);
      display: flex;
      flex-direction: column;
      box-shadow: none;
      transform: translateY(100%);
      transition: transform 250ms ease-out;
    }

    :host([open]) .dialog {
      transform: translateY(0);
    }

    @media (min-width: 768px) {
      .dialog {
        inset: auto;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: none;
        border-radius: 12px;
        max-height: 600px;
        max-width: 384px;
        width: calc(100% - 32px);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      }

      :host([open]) .dialog {
        transform: translate(-50%, -50%);
      }
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 8px 8px 20px;
      height: 55px;
      border-bottom: 1px solid var(--filterModalBorderColor);
    }

    .dialog-title {
      font: var(--filterModalHeaderFont);
      margin: 0;
    }

    .close-btn {
      appearance: none;
      background: none;
      border: none;
      color: var(--filterModalTextColor);
      cursor: pointer;
      font-size: 24px;
      line-height: 1;
      padding: 4px 8px;
    }

    .close-btn:focus-visible {
      outline: 2px solid var(--filterModalFocusRingColor, #fff);
      outline-offset: 2px;
      border-radius: 4px;
    }

    .dialog-body {
      overflow-y: auto;
      padding: 0 20px;
      flex: 1;
    }

    .dialog-footer {
      display: flex;
      gap: 16px;
      padding: 16px 20px;
      border-top: 1px solid var(--filterModalBorderColor);
    }

    .clear-btn {
      appearance: none;
      background: none;
      border: none;
      border-radius: 8px;
      color: var(--filterModalClearBtnTextColor);
      cursor: pointer;
      font: var(--filterModalBtnFont);
      padding: 10px 20px;

      @media (min-width: 768px) {
        &:hover {
          color: var(--filterModalClearBtnHoverTextColor);
          text-decoration: underline;
        }
      }
    }

    .clear-btn:focus-visible {
      outline: 2px solid var(--filterModalFocusRingColor);
      outline-offset: 2px;
    }

    .done-btn {
      appearance: none;
      background: var(--filterModalDoneBgColor);
      border: none;
      border-radius: 4px;
      color: var(--filterModalDoneTextColor);
      cursor: pointer;
      flex: 1;
      font: var(--filterModalBtnFont);
      padding: 10px 20px;

      &:active {
        background: var(--filterModalDoneActiveBgColor);
      }

      @media (min-width: 768px) {
        &:hover {
          background: var(--filterModalDoneHoverBgColor);
        }
      }
    }

    .done-btn:focus-visible {
      outline: 2px solid var(--filterModalFocusRingColor);
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .dialog, .backdrop { transition: none; }
    }
  `;

  const template = document.createElement('template');
  template.innerHTML = `
    <style>${style}</style>
    <div class="backdrop" part="backdrop"></div>
    <div class="dialog" role="dialog" aria-modal="true" aria-labelledby="filter-modal-title">
      <div class="dialog-header">
        <h2 class="dialog-title" id="filter-modal-title">Filters</h2>
        <button class="close-btn" type="button" aria-label="Close filters">&#x2715;</button>
      </div>
      <div class="dialog-body">
        <slot></slot>
      </div>
      <div class="dialog-footer">
        <button class="clear-btn" type="button">Clear All</button>
        <button class="done-btn" type="button">Show Movies</button>
      </div>
    </div>
  `;

  class FilterModal extends Component {
    static get observedAttributes() {
      return ['open'];
    }

    static get propsCamelConventionEnabled() {
      return true;
    }

    constructor() {
      super();
      this.initBool(['open']);
      this._triggerEl = null;
      this._focusableEls = [];
    }

    connectedCallback() {
      this._backdrop = this.select('.backdrop');
      this._closeBtn = this.select('.close-btn');
      this._clearBtn = this.select('.clear-btn');
      this._doneBtn = this.select('.done-btn');
      this._dialog = this.select('.dialog');

      this.attachEvent(this._backdrop, 'click', this._onClose);
      this.attachEvent(this._closeBtn, 'click', this._onClose);
      this.attachEvent(this._clearBtn, 'click', this._onClear);
      this.attachEvent(this._doneBtn, 'click', this._onDone);
      this.attachEvent(this._dialog, 'keydown', this._onKeydown);
    }

    template() {
      return template;
    }

    show(triggerEl) {
      this._triggerEl = triggerEl || document.activeElement;
      this.style.display = 'block';
      // Force reflow so transition fires
      void this.offsetHeight;
      requestAnimationFrame(() => {
        this.open = true;
        document.body.style.overflow = 'hidden';
        this._trapFocus();
      });
    }

    close() {
      this.open = false;
      this.style.display = '';
      document.body.style.overflow = '';
      if (this._triggerEl && this._triggerEl.focus) {
        this._triggerEl.focus();
      }
      this.dispatchEvent(new CustomEvent('filter-modal-close', {
        bubbles: true,
        composed: true
      }));
    }

    _onClose() {
      this.close();
    }

    _onClear() {
      this.dispatchEvent(new CustomEvent('filter-modal-clear', {
        bubbles: true,
        composed: true
      }));
    }

    _onDone() {
      this.close();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }

      if (e.key === 'Tab') {
        this._handleTabTrap(e);
      }
    }

    _trapFocus() {
      requestAnimationFrame(() => {
        this._updateFocusableEls();
        if (this._focusableEls.length) {
          this._focusableEls[0].focus();
        }
      });
    }

    _updateFocusableEls() {
      const shadowFocusable = this.shadowRoot.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const slotFocusable = this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), filter-facet, filter-group'
      );
      this._focusableEls = [...shadowFocusable, ...slotFocusable].filter(
        el => !el.disabled && el.offsetParent !== null
      );
    }

    _handleTabTrap(e) {
      this._updateFocusableEls();
      const els = this._focusableEls;
      if (!els.length) {
        return;
      }

      const first = els[0];
      const last = els[els.length - 1];
      const active = this.shadowRoot.activeElement || document.activeElement;

      if (e.shiftKey && (active === first || !els.includes(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !els.includes(active))) {
        e.preventDefault();
        first.focus();
      }
    }

    _attributeOpen(newValue) {
      if (newValue === '' || newValue === 'true') {
        this._trapFocus();
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }

  if (!window.customElements.get('filter-modal')) {
    window.customElements.define('filter-modal', FilterModal);
  }

  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = FilterModal;
  }
})();
